<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Gestión de Mascotas</h1>
          <p class="text-pink-100 mt-1">Administra las mascotas de todos los usuarios</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-pink-100">
            <svg class="h-6 w-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Mascotas</p>
            <p class="text-2xl font-semibold text-gray-900">{{ pets?.length || 0 }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Perros</p>
            <p class="text-2xl font-semibold text-gray-900">{{ dogCount }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Gatos</p>
            <p class="text-2xl font-semibold text-gray-900">{{ catCount }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <!-- Search Filter -->
        <div class="w-full lg:flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
          <input
            v-model="globalFilter"
            type="text"
            placeholder="Buscar por nombre, descripción o propietario..."
            class="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
        </div>

        <!-- Animal Type Filter -->
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Tipo de Animal</label>
          <Dropdown
            v-model="selectedAnimalType"
            :options="animalTypes"
            optionLabel="name"
            optionValue="id"
            placeholder="Todos los tipos"
            class="w-full"
            :showClear="true"
          />
        </div>

        <!-- Breed Filter -->
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Raza</label>
          <Dropdown
            v-model="selectedBreed"
            :options="filteredBreeds"
            optionLabel="name"
            optionValue="id"
            placeholder="Todas las razas"
            :disabled="!selectedAnimalType"
            class="w-full"
            :showClear="true"
          />
        </div>

        <!-- Gender Filter -->
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Género</label>
          <Dropdown
            v-model="selectedGender"
            :options="genderOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Todos los géneros"
            class="w-full"
            :showClear="true"
          />
        </div>

        <!-- Add Button -->
        <div class="w-full lg:w-auto">
          <button
            @click="router.push('/pets/create')"
            class="w-full lg:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Agregar Mascota
          </button>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        v-model:filters="filters"
        :value="filteredPets"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        tableStyle="min-width: 50rem"
        filterDisplay="menu"
        :globalFilterFields="['name', 'description', 'user.name', 'breed.name']"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Mascotas</h2>
            <IconField iconPosition="left">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText v-model="filters['global'].value" placeholder="Buscar mascotas..." />
            </IconField>
          </div>
        </template>
        
        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay mascotas</h3>
            <p class="mt-1 text-sm text-gray-500">Comienza agregando una nueva mascota.</p>
          </div>
        </template>
        
        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
            <p class="mt-2 text-gray-600">Cargando mascotas...</p>
          </div>
        </template>

        <Column field="image" header="Imagen" sortable style="width: 80px; min-width: 80px;">
          <template #body="{ data }">
            <div class="h-10 w-10 rounded-full overflow-hidden cursor-pointer hover:opacity-80 transition-opacity" @click="openImageModal(data.image || '/images/empty_image.png', data.name)">
              <img 
                :src="data.image || '/images/empty_image.png'" 
                :alt="data.name"
                class="h-full w-full object-cover"
                @error="$event.target.src = '/images/empty_image.png'"
              />
            </div>
          </template>
        </Column>

        <Column field="name" header="Nombre" sortable>
          <template #body="{ data }">
            <div>
              <div class="text-sm font-medium text-gray-900">{{ data.name }}</div>
              <div class="text-sm text-gray-500">{{ data.breed?.name }}</div>
            </div>
          </template>
        </Column>

        <Column field="user.name" header="Propietario" sortable>
          <template #body="{ data }">
            <div>
              <router-link 
                :to="`/user/${data.user?.id}`" 
                class="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
              >
                {{ data.user?.name }}
              </router-link>
              <div class="text-sm text-gray-500">{{ data.user?.email }}</div>
            </div>
          </template>
        </Column>

        <Column field="age" header="Edad" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{ data.age }} años</span>
          </template>
        </Column>

        <Column field="gender" header="Género" sortable>
          <template #body="{ data }">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                  :class="{
                    'bg-blue-100 text-blue-800 border border-blue-200': getGenderInSpanish(data.gender) === 'Macho',
                    'bg-pink-100 text-pink-800 border border-pink-200': getGenderInSpanish(data.gender) === 'Hembra'
                  }">
              <svg v-if="getGenderInSpanish(data.gender) === 'Macho'" class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2L13 5L11.5 6.5L13 8L10 11L7 8L8.5 6.5L7 5L10 2Z"/>
              </svg>
              <svg v-else class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2C8.34 2 7 3.34 7 5C7 6.66 8.34 8 10 8C11.66 8 13 6.66 13 5C13 3.34 11.66 2 10 2ZM10 18L8 16H12L10 18ZM10 14C8.34 14 7 12.66 7 11H13C13 12.66 11.66 14 10 14Z"/>
              </svg>
              {{ getGenderInSpanish(data.gender) }}
            </span>
          </template>
        </Column>

        <Column field="description" header="Descripción">
          <template #body="{ data }">
            <div class="text-sm text-gray-900 max-w-xs truncate" :title="data.description">
              {{ data.description || 'Sin descripción' }}
            </div>
          </template>
        </Column>

        <Column header="Acciones" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center space-x-2">
              <button
                @click="editPet(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style="background-color: #1d4ed8; color: #fff"
                title="Editar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                @click="deletePet(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                style="background-color: #be123c; color: #fff"
                title="Eliminar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Confirm Delete Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="'Eliminar Mascota'"
      :message="`¿Estás seguro de que quieres eliminar a ${petToDelete?.name}? Esta acción no se puede deshacer.`"
      :confirm-text="'Eliminar'"
      :cancel-text="'Cancelar'"
      @confirm="confirmDelete"
      @close="closeConfirmModal"
    />

    <!-- Image Modal -->
    <ImageModal
      :show="showImageModal"
      :image-url="selectedImage"
      :image-alt="selectedImageAlt"
      @close="closeImageModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useNotifications } from '../composables/useNotifications'
import { FilterMatchMode } from '@primevue/core/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Dropdown from 'primevue/dropdown'
import ConfirmModal from '../components/ConfirmModal.vue'
import ImageModal from '../components/ImageModal.vue'
import petService from '../services/petService'
import animalTypeService from '../services/animalTypeService'
import breedService from '../services/breedService'

const router = useRouter()
const authStore = useAuthStore()
const { showSuccess, showError } = useNotifications()

// Reactive data
const pets = ref([])
const breeds = ref([])
const animalTypes = ref([])
const loading = ref(false)

// Filters
const globalFilter = ref('')
const selectedAnimalType = ref('')
const selectedBreed = ref('')
const selectedGender = ref('')

// PrimeVue filters
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS }
})

// Gender options
const genderOptions = ref([
  { label: 'Macho', value: 'male' },
  { label: 'Hembra', value: 'female' }
])

const showConfirmModal = ref(false)
const showImageModal = ref(false)
const petToDelete = ref(null)
const selectedImage = ref('')
const selectedImageAlt = ref('')

// Computed properties
const dogCount = computed(() => {
  return pets.value.filter(pet => pet.breed?.animalType?.name === 'Perro').length
})

const catCount = computed(() => {
  return pets.value.filter(pet => pet.breed?.animalType?.name === 'Gato').length
})

const filteredBreeds = computed(() => {
  if (!selectedAnimalType.value) return breeds.value
  return breeds.value.filter(breed => breed.animalTypeId === selectedAnimalType.value)
})

const filteredPets = computed(() => {
  let filtered = pets.value

  // Filter by animal type
  if (selectedAnimalType.value) {
    filtered = filtered.filter(pet => pet.breed?.animalTypeId === selectedAnimalType.value)
  }

  // Filter by breed
  if (selectedBreed.value) {
    filtered = filtered.filter(pet => pet.breed?.id === selectedBreed.value)
  }

  // Filter by gender
  if (selectedGender.value) {
    filtered = filtered.filter(pet => pet.gender === selectedGender.value)
  }

  return filtered
})

// Watch for filter changes
watch(globalFilter, (newValue) => {
  filters.value.global.value = newValue
})

watch([selectedAnimalType, selectedBreed, selectedGender], () => {
  // Filters are handled by computed property filteredPets
  // Reset breed filter when animal type changes
  if (selectedAnimalType.value === '') {
    selectedBreed.value = ''
  }
})

// Methods
const loadPets = async () => {
  try {
    loading.value = true
    const response = await petService.getAllPets()
    pets.value = response.data
  } catch (error) {
    console.error('Error loading pets:', error)
    showError('Error al cargar las mascotas')
  } finally {
    loading.value = false
  }
}

const loadBreeds = async () => {
  try {
    const response = await breedService.getAllBreeds()
    breeds.value = response.data
  } catch (error) {
    console.error('Error loading breeds:', error)
  }
}

const loadAnimalTypes = async () => {
  try {
    const response = await animalTypeService.getAllAnimalTypes()
    animalTypes.value = response.data
  } catch (error) {
    console.error('Error loading animal types:', error)
  }
}

const getGenderInSpanish = (gender) => {
  const genderMap = {
    'male': 'Macho',
    'female': 'Hembra',
    'Macho': 'Macho',
    'Hembra': 'Hembra'
  }
  return genderMap[gender] || gender
}

const editPet = (pet) => {
  router.push(`/pets/${pet.id}/edit`)
}

const deletePet = (pet) => {
  petToDelete.value = pet
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
  petToDelete.value = null
}

const openImageModal = (imageUrl, imageAlt) => {
  selectedImage.value = imageUrl
  selectedImageAlt.value = imageAlt
  showImageModal.value = true
}

const closeImageModal = () => {
  showImageModal.value = false
  selectedImage.value = ''
  selectedImageAlt.value = ''
}

const confirmDelete = async () => {
  try {
    await petService.deletePet(petToDelete.value.id)
    showSuccess('Mascota eliminada exitosamente')
    closeConfirmModal()
    await loadPets()
  } catch (error) {
    console.error('Error deleting pet:', error)
    showError('Error al eliminar la mascota')
  }
}

// Initialize data
onMounted(async () => {
  await Promise.all([
    loadPets(),
    loadBreeds(),
    loadAnimalTypes()
  ])
})
</script>

<style scoped>
.card {
  @apply bg-white rounded-lg shadow-sm border border-gray-200;
}

:deep(.p-datatable) {
  border-radius: 0.5rem;
}

:deep(.p-datatable .p-datatable-header) {
  background: #f9fafb;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  padding: 1rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: #f9fafb;
}

:deep(.p-paginator) {
  background: #f9fafb;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  padding: 0.75rem 1rem;
}
</style>